/**
 * Identité de l'éditeur — le seul endroit à remplir.
 *
 * ── Pourquoi un fichier, et pas du texte dans les pages ─────────────────────
 * Les mêmes informations reviennent dans les mentions légales, les conditions
 * de vente, la page « Données personnelles », les factures et les courriels.
 * Recopiées, elles cessent d'être justes partout au premier changement — un
 * déménagement, un changement de forme juridique — et personne ne s'en aperçoit
 * avant qu'un client écrive à une adresse abandonnée.
 *
 * ── Ce qui vaut `null` s'affiche « À COMPLÉTER » ────────────────────────────
 * Jamais une valeur inventée : une adresse plausible mais fausse dans des
 * mentions légales est pire qu'un blanc, parce qu'elle a l'air vraie. Tant
 * qu'un champ obligatoire manque, les pages portent un avertissement visible,
 * et la liste de ce qui manque se calcule d'elle-même — il n'y a pas de
 * drapeau à baisser à la main le jour de la mise en vente.
 *
 * ── Ce que la loi impose de publier ─────────────────────────────────────────
 * Pour un service en ligne vendu à des particuliers :
 *   — LCEN, article 6 III-1 : identité de l'éditeur, contact, directeur de la
 *     publication, et identité de l'HÉBERGEUR ;
 *   — code de la consommation, article L111-1 : identité du vendeur, adresse,
 *     TÉLÉPHONE et adresse électronique, avant la conclusion du contrat ;
 *   — code de la consommation, article L612-1 : le MÉDIATEUR de la
 *     consommation auquel le professionnel adhère.
 * Un entrepreneur individuel doit en outre faire figurer « EI » ou
 * « entrepreneur individuel » avec son nom (article R123-237 du code de
 * commerce).
 */

export interface IdentiteEditeur {
  /**
   * Dénomination sociale, ou « Prénom NOM » pour un entrepreneur individuel —
   * sans « EI » : il est ajouté d'office, voir `nomVendeur`.
   */
  denomination: string | null;
  /**
   * Entreprise individuelle (micro-entreprise comprise).
   *
   * La mention « EI » suit alors le nom partout où il paraît — mentions
   * légales, conditions de vente, formulaire de rétractation — et la forme
   * juridique n'a plus à être saisie. Pas de capital à indiquer.
   */
  entrepreneurIndividuel: boolean;
  /** « SAS », « SASU », « EURL », « Entrepreneur individuel »… */
  formeJuridique: string | null;
  /** Capital social, en toutes lettres avec la devise. `null` si sans objet (EI). */
  capital: string | null;
  /** Adresse du siège, sur une seule ligne : numéro, voie, code postal, ville. */
  siege: string | null;
  /** Quatorze chiffres. */
  siret: string | null;
  /** Ville et numéro d'immatriculation, ou `null` pour un EI non commerçant. */
  rcs: string | null;
  /**
   * Numéro de TVA intracommunautaire, ou `null` en franchise en base : la page
   * affiche alors la mention de l'article 293 B du CGI, qui est obligatoire.
   */
  tvaIntracommunautaire: string | null;
  /** Vrai tant que l'éditeur ne facture pas la TVA (article 293 B du CGI). */
  franchiseDeTva: boolean;
  /** Personne physique responsable du contenu publié. */
  directeurPublication: string | null;
  /** Numéro appelable par un client. Exigé par l'article L111-1. */
  telephone: string | null;
}

export interface Hebergeur {
  nom: string | null;
  adresse: string | null;
  telephone: string | null;
  /** Pays des serveurs. L'engagement pris aux clients : Union européenne. */
  pays: string;
}

export interface Mediateur {
  nom: string | null;
  /** Adresse postale du médiateur. */
  adresse: string | null;
  /** Adresse du site où saisir le médiateur. */
  site: string | null;
}

export const EDITEUR: IdentiteEditeur = {
  denomination: null,
  entrepreneurIndividuel: true,
  formeJuridique: null,
  capital: null,
  siege: null,
  siret: null,
  rcs: null,
  tvaIntracommunautaire: null,
  franchiseDeTva: true,
  directeurPublication: null,
  telephone: null,
};

export const HEBERGEUR: Hebergeur = {
  nom: null,
  adresse: null,
  telephone: null,
  pays: 'Union européenne',
};

/**
 * Médiateur de la consommation.
 *
 * L'adhésion à un médiateur est OBLIGATOIRE pour vendre à des particuliers
 * (article L612-1 du code de la consommation) : elle se souscrit auprès d'un
 * médiateur agréé, et son nom doit figurer dans les conditions de vente.
 */
export const MEDIATEUR: Mediateur = { nom: null, adresse: null, site: null };

/**
 * Délégué à la protection des données.
 *
 * Non obligatoire ici — l'éditeur n'est ni un organisme public, ni engagé dans
 * un suivi systématique à grande échelle. `null` fait afficher l'adresse de
 * contact ordinaire, qui suffit alors.
 */
export const DPO: { nom: string | null; contact: string | null } = { nom: null, contact: null };

/**
 * Le nom du vendeur tel qu'il doit paraître : suivi de « EI » pour une
 * entreprise individuelle (article R123-237 du code de commerce). `null` tant
 * que la dénomination n'est pas renseignée.
 */
export function nomVendeur(): string | null {
  if (!EDITEUR.denomination) return null;
  const nom = EDITEUR.denomination.trim();
  return EDITEUR.entrepreneurIndividuel && !/(^|\s)EI$/.test(nom) ? `${nom} EI` : nom;
}

/** Forme juridique affichée — déduite pour une entreprise individuelle. */
export function formeJuridique(): string | null {
  return EDITEUR.entrepreneurIndividuel
    ? 'Entrepreneur individuel (micro-entreprise)'
    : EDITEUR.formeJuridique;
}

/** Champs obligatoires manquants, dans l'ordre où on les remplira. */
export function champsManquants(): string[] {
  const manque: string[] = [];
  const exige = (valeur: unknown, quoi: string) => { if (!valeur) manque.push(quoi); };

  exige(EDITEUR.denomination, EDITEUR.entrepreneurIndividuel
    ? 'nom et prénom de l’entrepreneur' : 'dénomination de l’éditeur');
  exige(formeJuridique(), 'forme juridique');
  exige(EDITEUR.siege, EDITEUR.entrepreneurIndividuel ? 'adresse' : 'adresse du siège');
  exige(EDITEUR.siret, 'SIRET');
  exige(EDITEUR.directeurPublication, 'directeur de la publication');
  exige(EDITEUR.telephone, 'téléphone');
  if (!EDITEUR.franchiseDeTva) exige(EDITEUR.tvaIntracommunautaire, 'numéro de TVA');
  exige(HEBERGEUR.nom, 'nom de l’hébergeur');
  exige(HEBERGEUR.adresse, 'adresse de l’hébergeur');
  exige(MEDIATEUR.nom, 'médiateur de la consommation');
  return manque;
}

/** Vrai quand toutes les mentions obligatoires sont renseignées. */
export function identiteComplete(): boolean {
  return champsManquants().length === 0;
}
